import { Fragment, useEffect, useState } from "react";
import { Loader2, XCircle } from "lucide-react";
import { useNowTicker } from "@/hooks/useNowTicker";
import { Button } from "@/components/ui/base/button";
import { Checkbox } from "@/components/ui/base/checkbox";
import { Label } from "@/components/ui/base/label";
import { RECONCILER_CATALOG } from "@cpos/shared/tools/reconcilers";
import { useStateMachine } from "@/core/arch/state_machine";
import { JobBloc } from "@/application/tools/job_bloc";
import { reconcileJobRepository } from "@/application/tools/job_repository";
import { ToolSheetShell } from "./ToolSheetShell";
import {
  chainedJobId,
  groupCounters,
  scannedTotal,
} from "./reconciler_tallies";
import { ReconcilerTallyTable } from "./ReconcilerTallyTable";
import { JobStatusBadge } from "./JobStatusBadge";
import { formatDuration } from "./job_display";
import type { ToolSheetProps } from "../tools";
import type { JobStatus } from "@/application/tools/types";

/**
 * Indeterminate progress. The sweep pages through the table and never learns a
 * total up front, so there is no denominator to show — only how far it has got.
 */
function ScanProgress({
  scanned,
  status,
  link,
}: {
  scanned: number;
  status: JobStatus;
  link: number;
}) {
  const active = status === "running" || status === "pending";

  return (
    <div className="space-y-1">
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>
          {scanned === 1 ? "1 merchant" : `${scanned} merchants`} scanned
          {link > 1 && ` across ${link} jobs`}
        </span>
        {active && <span>In progress…</span>}
      </div>
      <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
        {active ? (
          <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-400" />
        ) : (
          <div className="bg-muted-foreground/30 h-full w-full rounded-full" />
        )}
      </div>
    </div>
  );
}

export function RepairDatabaseJobSheet({ onClose }: ToolSheetProps) {
  const [state, bloc] = useStateMachine(
    () =>
      new JobBloc(reconcileJobRepository, "reconcile", {
        followChain: (job) => chainedJobId(job.result),
      }),
  );

  // Empty means every reconciler, which is both the scheduled behaviour and
  // what the runner does with an absent list — so the default needs no special
  // case here or on the backend.
  const [selected, setSelected] = useState<readonly string[]>([]);

  useEffect(() => {
    bloc.resumeIfRunning();
  }, [bloc]);

  function toggle(name: string, checked: boolean) {
    setSelected((current) =>
      checked ? [...current, name] : current.filter((entry) => entry !== name),
    );
  }

  const runsAll = selected.length === 0;

  const { phase } = state;
  const job =
    phase.kind === "watching" || phase.kind === "cancelling" ? phase.job : null;

  const isRunning = job?.status === "running" || job?.status === "pending";
  const now = useNowTicker(isRunning);

  const elapsed = job
    ? formatDuration(job.startedAt, job.completedAt, now)
    : null;
  const isTerminal =
    job?.status === "complete" ||
    job?.status === "failed" ||
    job?.status === "cancelled";
  const tallies = groupCounters(job?.counters ?? null);
  const scanned = scannedTotal(tallies, job?.processed ?? 0);

  return (
    <ToolSheetShell
      title="Repair Database"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          {phase.kind === "idle" && (
            <Button
              onClick={() =>
                bloc.kickoff(runsAll ? undefined : { reconcilers: selected })
              }
              className="w-full"
            >
              {runsAll
                ? "Run reconcile"
                : `Run reconcile (${selected.length} selected)`}
            </Button>
          )}
          {phase.kind === "kicking_off" && (
            <Button disabled className="w-full">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Starting…
            </Button>
          )}
          {phase.kind === "watching" && job?.status === "running" && (
            <Button
              variant="outline"
              onClick={() => bloc.cancel()}
              className="w-full"
            >
              Cancel
            </Button>
          )}
          {phase.kind === "cancelling" && (
            <Button disabled variant="outline" className="w-full">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Cancelling…
            </Button>
          )}
          {isTerminal && (
            <Button
              variant="outline"
              onClick={() => bloc.reset()}
              className="w-full"
            >
              Run again
            </Button>
          )}
        </div>
      }
    >
      <p className="text-muted-foreground text-sm">
        Sweeps every merchant in the database through every registered
        reconciler, re-deriving lifecycle state from the durable record —
        transaction history and persisted settings — rather than trusting cached
        flags. Pure derivation, so it is safe to re-run.
      </p>
      <p className="text-muted-foreground mt-3 text-sm">
        A full sweep does not fit one Lambda budget. When a job runs out of time
        it completes and enqueues a continuation from where it stopped; this
        panel follows the chain and reports the running totals.
      </p>
      <p className="text-muted-foreground mt-3 text-sm">
        This also runs nightly at 03:00 UTC. Trigger it here when a fix has just
        shipped and you do not want to wait for the schedule.
      </p>

      {phase.kind === "idle" && (
        <fieldset className="border-border mt-6 space-y-3 border-t pt-4">
          <legend className="sr-only">Reconcilers to run</legend>
          <p className="text-foreground text-xs font-medium">
            Reconcilers
            <span className="text-muted-foreground ml-2 font-normal">
              {runsAll ? "all (default)" : `${selected.length} selected`}
            </span>
          </p>
          {/* Three columns rather than a row per reconciler, so the label
              column is sized to the longest name and every description starts
              at the same x. */}
          <div className="grid grid-cols-[auto_max-content_1fr] items-start gap-x-3 gap-y-2">
            {RECONCILER_CATALOG.map((reconciler) => (
              <Fragment key={reconciler.name}>
                <Checkbox
                  id={`reconciler-${reconciler.name}`}
                  checked={selected.includes(reconciler.name)}
                  onCheckedChange={(checked) =>
                    toggle(reconciler.name, checked === true)
                  }
                />
                <Label
                  htmlFor={`reconciler-${reconciler.name}`}
                  className="text-foreground text-xs leading-tight whitespace-nowrap"
                >
                  {reconciler.label}
                </Label>
                {/* The wrapper carries the flag rather than the label, so the
                    grid's three-column alignment is unaffected. */}
                <span
                  data-reconciler={reconciler.name}
                  data-manual-only={reconciler.manualOnly ? "true" : "false"}
                  className="text-muted-foreground text-xs leading-tight"
                >
                  {reconciler.manualOnly && (
                    <span className="mr-1 font-medium text-amber-700">
                      Not run nightly —
                    </span>
                  )}
                  {reconciler.description}
                </span>
              </Fragment>
            ))}
          </div>
          <p className="text-subtle-foreground text-xs">
            Leave all unchecked to sweep with the scheduled reconcilers, as the
            nightly run does. The ones marked above are skipped unless you check
            them here.
          </p>
        </fieldset>
      )}

      {phase.kind === "error" && (
        <div className="mt-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{phase.message}</p>
        </div>
      )}

      {job && (
        <div className="border-border mt-6 space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <JobStatusBadge status={job.status} />
            {elapsed && (
              <span className="text-subtle-foreground text-xs">{elapsed}</span>
            )}
          </div>

          <ScanProgress
            scanned={scanned}
            status={job.status}
            link={state.link}
          />

          <ReconcilerTallyTable tallies={tallies} />

          {job.status === "complete" && scanned === 0 && (
            <div className="border-border bg-muted text-muted-foreground rounded-md border p-3 text-sm">
              No merchants found. Nothing to reconcile.
            </div>
          )}

          {job.status === "failed" && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">
                Pass did not complete cleanly
              </p>
              <p className="mt-1 text-xs text-red-600">
                Reconcilers that reported errors or abandoned records fail the
                job deliberately, and no continuation is enqueued — the counters
                above are still accurate for the work that did happen. Re-run
                once the cause is fixed.
              </p>
              {job.error && (
                <pre className="mt-2 overflow-x-auto text-xs text-red-600">
                  {job.error}
                </pre>
              )}
            </div>
          )}

          <p className="text-subtle-foreground text-xs">Job ID: {job.id}</p>
        </div>
      )}
    </ToolSheetShell>
  );
}
